import React, { useState, useEffect } from 'react';
import { Popover, Button, Divider, Typography, Space, message, Spin, Tree, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import type { TreeProps } from 'antd';
import {
  getUserSettings,
  setUserSettings,
} from '../../../../reducers/integrations/resources';
import _ from 'lodash';
import ChannelIcon from '../../../common/ChannelIcon';

const { Text } = Typography;



interface InboxFilter {
  type: 'bot' | 'location';
  channel?: 'sms' | 'webchat' | 'whatsapp';
  botmap_id?: number;
  location_id?: number | string;
  label: string | { label: string; sublabel?: string };
}

interface SelectedFilter {
  id: number | string;
  type: 'bot' | 'location';
  selected: boolean;
}

interface InboxFilterSettingsProps {
  disabled?: boolean;
  filters: InboxFilter[];
  companyId: string | number;
  onSave?: (filters: string[]) => void;
}

const InboxFilterSettings: React.FC<InboxFilterSettingsProps> = ({ 
  disabled = false, 
  filters = [],
  companyId,
  onSave,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);



  // Helper to get the company-specific settings key
  const getCompanyFiltersKey = () => `defaultInboxFilters_${companyId}`;

  // Build selected filters from store filters and user settings
  const buildSelectedFilters = async () => {
    setLoading(true);
    try {
      let settings = await getUserSettings();
      settings = settings.data;

      const temp: SelectedFilter[] = filters.map((filter) => {
        const id = filter.type === 'bot' ? filter.botmap_id : filter.location_id;
        return {
          id: id!,
          type: filter.type,
          selected: false,
        };
      });

      // Check for company-specific filters first, then fall back to legacy filters
      // Check for company-specific filters in the nested object
      let savedFilters = [];
      if (settings?.defaultInboxFilters) {
         if (Array.isArray(settings.defaultInboxFilters)) {
             savedFilters = settings.defaultInboxFilters;
         } else {
             savedFilters = settings.defaultInboxFilters[companyId];
         }
      }

      if (savedFilters && savedFilters.length > 0) {
        savedFilters.forEach((dif: { type: string; id: number | string }) => {
          const idx = temp.findIndex(
            (elem) => elem.type === dif.type && elem.id === dif.id
          );
          if (idx >= 0) {
            temp[idx].selected = true;
          }
        });
      }

      setSelectedFilters(temp);
    } catch (error) {
      console.error('Error fetching filter data:', error);
      message.error('Failed to load filter settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && filters.length > 0) {
      buildSelectedFilters();
    }
  }, [open, filters]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const filtersToSave = selectedFilters
        .filter((f) => f.selected)
        .map((e) => ({ id: e.id, type: e.type }));

      // Get updated data in case availability toggle above is updated
      const res = await getUserSettings();
      const settings = res.data;
      
      if (settings) {
        let selectedDays = [false, true, true, true, true, true, false];
        const { selectedDays: sDays } = settings;

        // Construct selectedDays for API
        selectedDays = selectedDays.fill(false);
        if (sDays) {
          sDays.forEach((days: number) => {
            selectedDays[days] = true;
          });
        }

        const data = {
          ...settings,
          selectedDays,
          defaultInboxFilters: {
            ...(settings.defaultInboxFilters && !Array.isArray(settings.defaultInboxFilters) ? settings.defaultInboxFilters : {}),
            [companyId]: filtersToSave
          }
        };
        // Remove location
        delete data.location;

        await setUserSettings(data);
        message.success('Inbox filter settings saved');
        setOpen(false);

        if (onSave) {
          const savedIds = filtersToSave.map(f => String(f.id));
          // If no filters selected, we default to 'allContacts' logically, 
          // but we save empty array.
          // Let's pass what we saved. The parent handles the 'allContacts' fallback if needed.
           onSave(savedIds.length > 0 ? savedIds : ['allContacts']);
        }
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getFilterLabel = (filter: InboxFilter): string => {
    if (typeof filter.label === 'object') {
      return filter.label.label;
    }
    return filter.label;
  };

  const getFilterSublabel = (filter: InboxFilter): string | undefined => {
    if (typeof filter.label === 'object') {
      return filter.label.sublabel;
    }
    return undefined;
  };

  const buildTreeData = () => {
    const children: any[] = [];

    // Texting Numbers (SMS bots) - show all, don't filter out duplicates
    const textingNumbers = filters.filter((f) => f.type === 'bot' && f.channel === 'sms');
    if (textingNumbers.length > 0) {
      const botNodes = textingNumbers.map((filter) => {
        const sublabel = getFilterSublabel(filter);
        const label = getFilterLabel(filter);
        return {
          title: sublabel ? (
            <Tooltip placement="right" title={sublabel}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{label}</span>
              </div>
            </Tooltip>
          ) : label,
          value: `bot-${filter.botmap_id}`,
          key: `bot-${filter.botmap_id}`,
        };
      });

      children.push({
        title: 'All Texting Numbers',
        value: 'allTextingNumbers',
        key: 'allTextingNumbers',
        children: botNodes,
      });
    }

    // Web Chat bots - show all
    const webchatBots = filters.filter((f) => f.type === 'bot' && f.channel === 'webchat');
    if (webchatBots.length > 0) {
      const webchatNodes = webchatBots.map((filter) => ({
        title: (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{getFilterLabel(filter)}</span>
          </div>
        ),
        value: `bot-${filter.botmap_id}`,
        key: `bot-${filter.botmap_id}`,
      }));

      children.push({
        title: 'All Web Chat',
        value: 'allWebChat',
        key: 'allWebChat',
        children: webchatNodes,
      });
    }

    // WhatsApp senders (ADR-025)
    const whatsappBots = filters.filter((f) => f.type === 'bot' && f.channel === 'whatsapp');
    if (whatsappBots.length > 0) {
      const whatsappNodes = whatsappBots.map((filter) => ({
        title: (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <ChannelIcon channel="whatsapp" size={12} />
            <span>{getFilterLabel(filter)}</span>
          </div>
        ),
        value: `bot-${filter.botmap_id}`,
        key: `bot-${filter.botmap_id}`,
      }));

      children.push({
        title: (<span><ChannelIcon channel="whatsapp" size={12} style={{ marginRight: 4 }} />All WhatsApp</span>),
        value: 'allWhatsApp',
        key: 'allWhatsApp',
        children: whatsappNodes,
      });
    }

    // Locations - show all
    const locations = filters.filter((f) => f.type === 'location');
    if (locations.length > 0) {
      const locationNodes = locations.map((filter) => ({
        title: (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{getFilterLabel(filter)}</span>
          </div>
        ),
        value: `location-${filter.location_id}`,
        key: `location-${filter.location_id}`,
      }));

      children.push({
        title: 'All Locations',
        value: 'allLocations',
        key: 'allLocations',
        children: locationNodes,
      });
    }

    // Wrap everything under "All Contacts"
    const treeData = [
      {
        title: 'All Contacts',
        value: 'allContacts',
        key: 'allContacts',
        children,
      },
    ];

    return treeData;
  };

  const handleTreeSelectChange = (newValues: string[]) => {
    const updatedFilters = selectedFilters.map((filter) => {
      const isSelected = newValues.some((val) => {
        if (filter.type === 'bot') {
          return val === `bot-${filter.id}`;
        } else if (filter.type === 'location') {
          return val === `location-${filter.id}`;
        }
        return false;
      });
      return { ...filter, selected: isSelected };
    });

    setSelectedFilters(updatedFilters);
  };

  const getSelectedValues = () => {
    return selectedFilters
      .filter((f) => f.selected)
      .map((f) => (f.type === 'bot' ? `bot-${f.id}` : `location-${f.id}`));
  };

  const onTreeCheck: TreeProps['onCheck'] = (checkedKeysValue) => {
    const checkedKeys = Array.isArray(checkedKeysValue) ? checkedKeysValue : checkedKeysValue.checked;
    handleTreeSelectChange(checkedKeys as string[]);
  };

  const content = (
    <div id="inbox-filter-settings-container" style={{ width: 320, paddingTop: 0 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 8 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Select your default inbox filters. These filters are applied when you open the inbox.
            Leaving all unchecked will default to all available contacts.
          </Text>

          <div className="scrollableListDiv" style={{ height: 220, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: 8, marginBottom: 12 }}>
              <Tree
                checkable
                defaultExpandedKeys={['allContacts', 'allTextingNumbers', 'allWebChat', 'allWhatsApp', 'allLocations']}
                checkedKeys={getSelectedValues()}
                onCheck={onTreeCheck}
                treeData={buildTreeData()}
                selectable={false}
              />
          </div>

          <Divider style={{ margin: '12px 0 8px' }} />
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button size="small" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" size="small" loading={saving} onClick={saveSettings}>
                Save
              </Button>
            </Space>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title={<div style={{ padding: '0 8px', marginBottom: -8 }}><Text strong>Default Inbox Filters</Text></div>}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Button
        type="text"
        icon={<SettingOutlined style={{ color: '#bbb' }} />}
        size="small"
        style={{ marginLeft: 4, backgroundColor: '#f5f5f5' }}
        disabled={disabled}
      />
    </Popover>
  );
};

export default InboxFilterSettings;
